import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { ShieldCheck, UserCheck, UserPlus, ChevronRight } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';

const PRIMARY_BTN = {
  bg: '#fafafa',
  color: '#0a0a0a',
  borderRadius: 'full',
  fontWeight: '600',
  fontSize: '13px',
  h: '36px',
  _hover: { opacity: 0.85 },
  _active: { opacity: 0.7 },
};

const OUTLINE_BTN = {
  bg: 'transparent',
  borderRadius: 'full',
  fontWeight: '500',
  fontSize: '13px',
  h: '36px',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'rgba(255,255,255,0.6)',
  _hover: {
    borderColor: 'rgba(255,255,255,0.3)',
    color: 'rgba(255,255,255,0.9)',
    bg: 'rgba(255,255,255,0.04)',
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Initials avatar for known reviewers.
const Avatar = ({ name }) => {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
  return (
    <Box
      w="32px" h="32px" borderRadius="full" flexShrink={0}
      bg="rgba(255,255,255,0.08)" border="1px solid rgba(255,255,255,0.12)"
      display="flex" alignItems="center" justifyContent="center"
    >
      <Text fontSize="11px" fontWeight="600" color="rgba(255,255,255,0.7)">{initials || '?'}</Text>
    </Box>
  );
};

const ComplianceReviewModal = ({ isOpen, onClose, campaignId, campaignName }) => {
  // 'select' | 'invite'
  const [mode, setMode] = useState('select');
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [email, setEmail] = useState('');
  const [contextNote, setContextNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState(null);

  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Fetch known reviewers (advisors the current user has previously worked with).
  const { data: knownReviewers = [], isLoading: loadingReviewers } = useQuery({
    queryKey: ['knownReviewers', user?.id],
    queryFn: async () => {
      const { data } = await api.get('/api/compliance/reviewers');
      return data;
    },
    enabled: Boolean(user?.id && isOpen),
    staleTime: 5 * 60 * 1000,
  });

  // Reset state when modal opens.
  useEffect(() => {
    if (isOpen) {
      setMode('select');
      setSelectedReviewer(null);
      setEmail('');
      setContextNote('');
      setFieldError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const emailValid = EMAIL_RE.test(trimmedEmail);

  const invalidateCampaigns = useCallback(() => {
    if (user?.id) queryClient.invalidateQueries({ queryKey: ['campaigns', user.id] });
  }, [user?.id, queryClient]);

  const showSuccessAndClose = useCallback((msg) => {
    invalidateCampaigns();
    toast({
      title: 'Reviewer added',
      description: msg,
      status: 'success',
      duration: 4000,
      isClosable: true,
      position: 'bottom-right',
    });
    onClose();
  }, [invalidateCampaigns, toast, onClose]);

  const handleError = useCallback((err, defaultTitle = "Couldn't add reviewer") => {
    const code = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (code === 400 && typeof detail === 'string') {
      setFieldError(detail);
    } else {
      toast({
        title: defaultTitle,
        description: typeof detail === 'string' ? detail : err?.message || 'An unexpected error occurred.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  }, [toast]);

  // Direct-add a known reviewer (no email link).
  const handleSelectReviewer = useCallback(async (reviewer) => {
    if (!campaignId || isSubmitting) return;
    setSelectedReviewer(reviewer.user_id);
    setIsSubmitting(true);
    try {
      await api.post(`/api/compliance/campaigns/${campaignId}/reviewers/${reviewer.user_id}`);
      showSuccessAndClose(`${reviewer.display_name} can now review this campaign.`);
    } catch (err) {
      setSelectedReviewer(null);
      handleError(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [campaignId, isSubmitting, showSuccessAndClose, handleError]);

  // Email-invite a new reviewer.
  const handleInvite = useCallback(async () => {
    if (!campaignId) return;
    if (!trimmedEmail) { setFieldError("Enter the reviewer's email address."); return; }
    if (!emailValid) { setFieldError("That doesn't look like a valid email."); return; }
    setFieldError(null);
    setIsSubmitting(true);
    try {
      const { data } = await api.post(
        `/api/compliance/campaigns/${campaignId}/invite`,
        { email: trimmedEmail, context_note: contextNote.trim() || null },
      );
      showSuccessAndClose(
        data?.invited_email
          ? `${data.invited_email} will receive an invitation email.`
          : 'The reviewer will receive an invitation email.',
      );
    } catch (err) {
      handleError(err, "Couldn't send invitation");
    } finally {
      setIsSubmitting(false);
    }
  }, [campaignId, emailValid, trimmedEmail, contextNote, showSuccessAndClose, handleError]);

  const hasKnownReviewers = knownReviewers.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="rgba(0,0,0,0.85)" />
      <ModalContent
        bg="#111111"
        borderColor="rgba(255,255,255,0.10)"
        borderWidth="1px"
        borderRadius="10px"
        maxW="480px"
      >
        <ModalHeader
          color="#fafafa"
          fontSize="md"
          fontWeight="600"
          letterSpacing="-0.01em"
          borderBottom="1px solid"
          borderColor="rgba(255,255,255,0.08)"
          pb={4}
        >
          <HStack spacing={2.5}>
            <Box
              p={1.5} borderRadius="6px"
              bg="rgba(255,255,255,0.06)"
              border="1px solid rgba(255,255,255,0.08)"
              display="flex" alignItems="center"
            >
              <ShieldCheck size={13} strokeWidth={1.75} color="rgba(255,255,255,0.75)" />
            </Box>
            <Text>Send for compliance review</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton
          color="rgba(255,255,255,0.4)"
          borderRadius="full"
          isDisabled={isSubmitting}
          _hover={{ color: 'rgba(255,255,255,0.75)', bg: 'rgba(255,255,255,0.06)' }}
        />

        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">

            {/* ── Known reviewers ───────────────────────────────────── */}
            {loadingReviewers ? (
              <HStack justify="center" py={3}>
                <Spinner size="sm" color="rgba(255,255,255,0.3)" />
              </HStack>
            ) : hasKnownReviewers ? (
              <>
                <Text fontSize="xs" fontWeight="500" color="rgba(255,255,255,0.4)" letterSpacing="0.06em" textTransform="uppercase">
                  Previous reviewers
                </Text>
                <VStack spacing={1} align="stretch">
                  {knownReviewers.map((r) => (
                    <Box
                      key={r.user_id}
                      as="button"
                      display="flex"
                      alignItems="center"
                      w="100%"
                      px={3} py={2.5}
                      borderRadius="8px"
                      border="1px solid"
                      borderColor={selectedReviewer === r.user_id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)'}
                      bg={selectedReviewer === r.user_id ? 'rgba(255,255,255,0.06)' : 'transparent'}
                      _hover={{ bg: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.18)' }}
                      _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                      disabled={isSubmitting}
                      onClick={() => handleSelectReviewer(r)}
                      textAlign="left"
                      transition="all 0.12s"
                    >
                      <HStack spacing={3} flex={1}>
                        <Avatar name={r.display_name} />
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" fontWeight="500" color="#fafafa" noOfLines={1}>{r.display_name}</Text>
                          <Text fontSize="xs" color="rgba(255,255,255,0.4)" noOfLines={1}>{r.email}</Text>
                        </Box>
                        {selectedReviewer === r.user_id && isSubmitting
                          ? <Spinner size="xs" color="rgba(255,255,255,0.5)" />
                          : <ChevronRight size={14} color="rgba(255,255,255,0.3)" />
                        }
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </>
            ) : null}

            {/* ── Invite new reviewer ───────────────────────────────── */}
            {hasKnownReviewers && mode === 'select' ? (
              <>
                <Divider borderColor="rgba(255,255,255,0.07)" />
                <Box
                  as="button"
                  display="flex"
                  alignItems="center"
                  w="100%"
                  px={3} py={2.5}
                  borderRadius="8px"
                  border="1px dashed rgba(255,255,255,0.12)"
                  bg="transparent"
                  _hover={{ bg: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.22)' }}
                  onClick={() => setMode('invite')}
                  textAlign="left"
                  transition="all 0.12s"
                >
                  <HStack spacing={3}>
                    <Box
                      w="32px" h="32px" borderRadius="full" flexShrink={0}
                      bg="rgba(255,255,255,0.05)" border="1px dashed rgba(255,255,255,0.15)"
                      display="flex" alignItems="center" justifyContent="center"
                    >
                      <UserPlus size={13} color="rgba(255,255,255,0.5)" />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="500" color="rgba(255,255,255,0.7)">Invite a new reviewer</Text>
                      <Text fontSize="xs" color="rgba(255,255,255,0.35)">Send an email invite to someone new</Text>
                    </Box>
                  </HStack>
                </Box>
              </>
            ) : (
              /* No known reviewers, or user clicked "Invite new reviewer" */
              <>
                {hasKnownReviewers && (
                  <Button
                    variant="ghost"
                    size="xs"
                    color="rgba(255,255,255,0.4)"
                    _hover={{ color: 'rgba(255,255,255,0.7)', bg: 'transparent' }}
                    onClick={() => { setMode('select'); setFieldError(null); }}
                    alignSelf="flex-start"
                    px={0}
                    mb={-1}
                  >
                    ← Back
                  </Button>
                )}

                {!hasKnownReviewers && (
                  <Text fontSize="sm" color="#a3a3a3" lineHeight="1.65">
                    Invite a compliance officer to review the message drafts
                    {campaignName ? (
                      <> in <Text as="span" color="#fafafa" fontWeight="500">{campaignName}</Text></>
                    ) : ' in this campaign'}.
                    They'll receive an email link to sign in and review.
                  </Text>
                )}

                <FormControl isInvalid={Boolean(fieldError)} isDisabled={isSubmitting}>
                  <FormLabel
                    fontSize="xs" fontWeight="500" color="rgba(255,255,255,0.55)"
                    letterSpacing="0.04em" textTransform="uppercase" mb={1.5}
                  >
                    Reviewer email
                  </FormLabel>
                  <Input
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (fieldError) setFieldError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSubmitting) { e.preventDefault(); handleInvite(); } }}
                    type="email"
                    placeholder="reviewer@firm.com"
                    bg="#0a0a0a"
                    color="#fafafa"
                    borderColor={fieldError ? 'rgba(255,90,90,0.5)' : 'rgba(255,255,255,0.12)'}
                    borderRadius="8px"
                    fontSize="sm"
                    h="38px"
                    _placeholder={{ color: 'rgba(255,255,255,0.28)' }}
                    _hover={{ borderColor: 'rgba(255,255,255,0.22)' }}
                    _focus={{ borderColor: fieldError ? 'rgba(255,120,120,0.7)' : 'rgba(255,255,255,0.32)', boxShadow: 'none' }}
                    autoFocus
                  />
                  {fieldError && (
                    <Text fontSize="xs" color="rgba(255,120,120,0.85)" mt={1.5}>{fieldError}</Text>
                  )}
                </FormControl>

                <FormControl isDisabled={isSubmitting}>
                  <FormLabel
                    fontSize="xs" fontWeight="500" color="rgba(255,255,255,0.55)"
                    letterSpacing="0.04em" textTransform="uppercase" mb={1.5}
                  >
                    Note <Text as="span" opacity={0.55}>(optional)</Text>
                  </FormLabel>
                  <Textarea
                    value={contextNote}
                    onChange={(e) => setContextNote(e.target.value)}
                    placeholder="e.g. Q1 outreach — please approve by Friday."
                    bg="#0a0a0a"
                    color="#fafafa"
                    borderColor="rgba(255,255,255,0.12)"
                    borderRadius="8px"
                    fontSize="sm"
                    rows={3}
                    resize="none"
                    _placeholder={{ color: 'rgba(255,255,255,0.28)' }}
                    _hover={{ borderColor: 'rgba(255,255,255,0.22)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.32)', boxShadow: 'none' }}
                  />
                </FormControl>
              </>
            )}

          </VStack>
        </ModalBody>

        {/* Footer only shown in invite mode */}
        {(mode === 'invite' || !hasKnownReviewers) && (
          <ModalFooter borderTop="1px solid" borderColor="rgba(255,255,255,0.08)" pt={4}>
            <HStack spacing={2}>
              <Button {...OUTLINE_BTN} onClick={handleClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                {...PRIMARY_BTN}
                onClick={handleInvite}
                isLoading={isSubmitting}
                loadingText="Sending…"
                isDisabled={!trimmedEmail || !emailValid || isSubmitting}
              >
                Send invitation
              </Button>
            </HStack>
          </ModalFooter>
        )}

        {/* Footer for select mode with known reviewers — just a cancel */}
        {mode === 'select' && hasKnownReviewers && (
          <ModalFooter borderTop="1px solid" borderColor="rgba(255,255,255,0.08)" pt={4}>
            <Button {...OUTLINE_BTN} onClick={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </ModalFooter>
        )}

      </ModalContent>
    </Modal>
  );
};

export default ComplianceReviewModal;
